import { useDebounce } from '@/hooks/use-debounce';
import api from '@/services/api';
import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  ViewProps,
} from 'react-native';
import { DataTable } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { Pagination } from '@/components/ui/pagination';
import { ThemedText } from '../themed-text';
import { Button } from '@/components/ui/button';

export type ColumnDefinition<T> = {
  header: string;
  accessor: keyof T | ((item: T) => React.ReactNode);
  width?: number | string;
  flex?: number;
};

type ControlledDataTableSelectProps<T> = {
  control: Control<any>;
  name: string;
  label?: string;
  disabled?: boolean;
  fetchEndpoint: string;
  searchParam?: string;
  /** Filtros fixos mandados junto do fetch (ex.: `{ isSubscription: true }`). */
  extraParams?: Record<string, string | number | boolean>;
  modalTitle: string;
  columns: ColumnDefinition<T>[];
  initialSelectedName?: string;
  valueAccessor: (item: T) => string;
  displayAccessor: (item: T) => string;
  onSelectItem?: (item: T | null) => void; // Added onSelectItem prop
  /** Posição do modal: 'center' (padrão) ou 'top'. Padrão: center. */
  dropdownPlacement?: 'top' | 'center';
} & ViewProps;

export function ControlledDataTableSelect<T>({
  control,
  name,
  label,
  disabled = false,
  fetchEndpoint,
  searchParam = 'q',
  extraParams,
  modalTitle,
  columns,
  initialSelectedName,
  valueAccessor,
  displayAccessor,
  onSelectItem, // Destructure prop
  dropdownPlacement = 'center',
  ...viewProps
}: ControlledDataTableSelectProps<T>) {
  const [modalVisible, setModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayName, setDisplayName] = useState(initialSelectedName || '');

  // Local selection state inside modal
  const [tempSelectedValue, setTempSelectedValue] = useState<string | null>(null);
  const [tempSelectedItem, setTempSelectedItem] = useState<T | null>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10; // Fixed at 10

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const value = useWatch({
    control,
    name,
  });

  useEffect(() => {
    if (initialSelectedName) {
      setDisplayName(initialSelectedName);
    } else if (!value) {
      setDisplayName('');
    }
  }, [value, initialSelectedName]);

  const fetchData = useCallback(
    async (page: number, limit: number, query: string) => {
      setIsLoading(true);
      try {
        const params: any = {
          page,
          limit,
          ...extraParams,
        };
        if (query) {
          params[searchParam] = query;
        }

        const response = await api.get<any>(`/${fetchEndpoint}`, {
          params,
        });

        const responseData = response.data;
        const items = Array.isArray(responseData) ? responseData : responseData?.data || [];
        const total = responseData?.total || items.length;
        const totalP = responseData?.totalPages || Math.ceil(total / limit) || 1;

        setData(items);
        setTotalPages(totalP);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchEndpoint, searchParam, JSON.stringify(extraParams ?? {})],
  );

  useEffect(() => {
    if (modalVisible) {
      fetchData(currentPage, itemsPerPage, debouncedSearchQuery);
    }
  }, [modalVisible, currentPage, itemsPerPage, debouncedSearchQuery, fetchData]);

  useEffect(() => {
    if (modalVisible) {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, modalVisible]);

  const openModal = useCallback(() => {
    if (disabled) return;
    setSearchQuery('');
    setData([]);
    setTempSelectedValue(value); // Initialize with current value
    setTempSelectedItem(null); // Reset item ref
    setModalVisible(true);
  }, [disabled, value]);

  const closeModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur }, fieldState: { error } }) => {
        const handleRowSelect = (item: T) => {
          const val = valueAccessor(item);
          setTempSelectedValue(val === tempSelectedValue ? null : val);
          setTempSelectedItem(val === tempSelectedValue ? null : item);
        };

        const handleConfirm = () => {
          if (tempSelectedValue !== null) {
            onChange(tempSelectedValue);
            if (tempSelectedItem) {
              setDisplayName(displayAccessor(tempSelectedItem));
              onSelectItem?.(tempSelectedItem);
            } else if (tempSelectedValue === value) {
              // If keeping same value, displayName stays same, no item update needed unless we want to refetch
            }
          } else {
            // Clearing selection
            onChange(null);
            setDisplayName('');
            onSelectItem?.(null);
          }
          onBlur();
          closeModal();
        };

        const isSelected = (item: T) => valueAccessor(item) === tempSelectedValue;

        return (
          <View className="relative mb-4 w-full" {...viewProps}>
            {label && <ThemedText type="labelInput">{label}</ThemedText>}
            <Pressable
              onPress={openModal}
              disabled={disabled}
              className={`
                min-h-[48px] flex-row items-center justify-between rounded-xl border bg-card px-4 py-3
                ${error ? 'border-error-500' : 'border-neutral-200'}
                ${disabled ? 'opacity-50' : ''}
              `}
            >
              <Text
                className={`
                  text-base
                  ${displayName ? 'text-foreground' : 'text-neutral-400'}
                `}
              >
                {displayName || `Selecionar ${label?.toLowerCase()}`}
              </Text>
              <Ionicons
                name="chevron-down"
                size={20}
                className={error ? 'text-red-500' : 'text-neutral-400'}
              />
            </Pressable>

            {error && <Text className="mt-1 text-xs text-red-500">{error.message}</Text>}

            <Modal
              visible={modalVisible}
              transparent
              animationType="fade"
              onRequestClose={closeModal}
            >
              <SafeAreaView className="flex-1 bg-black/50">
                {/* A busca abre focada: o cartão encolhe para caber acima do
                    teclado e as linhas rolam na vertical. */}
                <KeyboardAvoidingView behavior="padding" className="flex-1 justify-center">
                  <View className="m-5 mx-auto max-h-[90%] w-[95%] flex-1 overflow-hidden rounded-2xl bg-card shadow-lg">
                    <View className="flex-row items-center justify-between bg-card p-4">
                      <Text className="font-bold text-lg text-card-foreground">{modalTitle}</Text>
                      <Pressable onPress={closeModal}>
                        <Ionicons name="close-circle" size={28} className="text-neutral-400" />
                      </Pressable>
                    </View>

                    {/* Search Bar */}
                    <View className="bg-card px-4 pb-4">
                      <View className="relative">
                        <View className="absolute left-3.5 top-3.5 z-10 justify-center">
                          <Ionicons name="search-outline" size={20} className="text-neutral-400" />
                        </View>
                        <TextInput
                          value={searchQuery}
                          onChangeText={setSearchQuery}
                          placeholder="Buscar..."
                          placeholderTextColor="#A1A1AA"
                          autoFocus
                          className="rounded-xl border border-neutral-200 bg-card py-3 pl-11 pr-4 text-base text-foreground"
                        />
                      </View>
                    </View>

                    {isLoading && data.length === 0 ? (
                      <View className="flex-1 items-center justify-center bg-card">
                        <ActivityIndicator size="large" className="text-primary" />
                      </View>
                    ) : (
                      <ScrollView className="flex-1 bg-card" keyboardShouldPersistTaps="handled">
                        <ScrollView
                          horizontal
                          contentContainerStyle={{ flexGrow: 1 }}
                          keyboardShouldPersistTaps="handled"
                        >
                          <View style={{ minWidth: '100%' }}>
                            <DataTable>
                              <DataTable.Header>
                                <DataTable.Title style={{ maxWidth: 50, justifyContent: 'center' }}>
                                  <Ionicons
                                    name="checkmark-circle-outline"
                                    size={20}
                                    className="text-neutral-400"
                                  />
                                </DataTable.Title>

                                {columns.map((col, titleIdx) => (
                                  <DataTable.Title
                                    key={titleIdx}
                                    style={[
                                      col.width ? { width: col.width as any, flex: 0 } : {},
                                      col.flex ? { flex: col.flex } : {},
                                    ]}
                                  >
                                    <Text className="font-bold text-foreground" numberOfLines={1}>
                                      {col.header}
                                    </Text>
                                  </DataTable.Title>
                                ))}
                              </DataTable.Header>

                              {data.length === 0 ? (
                                <View className="py-8">
                                  <Text className="text-center text-sm text-neutral-500">
                                    Nenhum resultado encontrado.
                                  </Text>
                                </View>
                              ) : (
                                data.map((item, rowIdx) => {
                                  const selected = isSelected(item);
                                  return (
                                    <DataTable.Row
                                      key={rowIdx}
                                      onPress={() => handleRowSelect(item)}
                                    >
                                      <DataTable.Cell
                                        style={{ maxWidth: 50, justifyContent: 'center' }}
                                      >
                                        <View
                                          className={`h-6 w-6 items-center justify-center rounded-full border ${selected ? 'border-primary bg-primary' : 'border-neutral-300'}`}
                                        >
                                          {selected && (
                                            <Ionicons name="checkmark" size={16} color="white" />
                                          )}
                                        </View>
                                      </DataTable.Cell>

                                      {columns.map((col, cellIdx) => (
                                        <DataTable.Cell
                                          key={cellIdx}
                                          style={[
                                            col.width ? { width: col.width as any, flex: 0 } : {},
                                            col.flex ? { flex: col.flex } : {},
                                          ]}
                                        >
                                          {typeof col.accessor === 'function' ? (
                                            col.accessor(item)
                                          ) : (
                                            <Text
                                              className="text-sm text-foreground"
                                              numberOfLines={2}
                                            >
                                              {String(item[col.accessor] || '-')}
                                            </Text>
                                          )}
                                        </DataTable.Cell>
                                      ))}
                                    </DataTable.Row>
                                  );
                                })
                              )}
                            </DataTable>
                          </View>
                        </ScrollView>
                      </ScrollView>
                    )}

                    {/* Footer with Pagination and Confirm Button */}
                    <View className="gap-4 border-t border-border bg-card p-4">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        // removed itemsPerPageOptions to hide selector
                      />

                      <Button onPress={handleConfirm} disabled={!tempSelectedValue}>
                        <ThemedText className="font-bold text-white">Confirmar Seleção</ThemedText>
                      </Button>
                    </View>
                  </View>
                </KeyboardAvoidingView>
              </SafeAreaView>
            </Modal>
          </View>
        );
      }}
    />
  );
}
